import { useEffect, useMemo } from "react"
import { Line } from "react-chartjs-2"
import { Chart, registerables } from "chart.js"
import dayjs from "lib/dayjs"

import {
  useLazyGetVendorCallContractorPerformanceQuery,
  useLazyGetVendorCallLogQuery,
  useLazyGetVendorCallStatsQuery,
} from "store/api/vendorApi"
import { ApiTypes, UITypes } from "types"

import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DateRangeTabsFilter } from "components/common/DateRangeTabsFilter"
import {
  formatCustomDateRangeLabel,
  getDateRangeParams,
  getFilterValueFromParams,
} from "components/common/DateRangeTabsFilter/dateRangeFilterUtils"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"
import { getCallOutcomeLabel } from "pages/contractor/MonthlyEngagementPage/customerCalls"
import outcomeStyles from "pages/contractor/MonthlyEngagementPage/CustomerCallsSection.module.scss"

import styles from "./index.module.scss"

Chart.register(...registerables)

const headerCells: UITypes.TableHeaderCell[] = [
  { title: "Date & Time", name: "occurred_at", type: "default" },
  { title: "Customer", name: "customer", type: "sort-search" },
  { title: "Contractor", name: "contractor", type: "sort-search" },
  { title: "Calling Number", name: "calling_number", type: "default" },
  {
    title: "Outcome",
    name: "outcome",
    type: "default",
    cellComponent: ({ cell }) => {
      const outcome = cell.getValue()?.value
      if (outcome !== "answered" && outcome !== "missed") return null

      return (
        <span
          className={`${outcomeStyles.outcome} ${
            outcome === "answered" ? outcomeStyles.outcomeAnswered : outcomeStyles.outcomeMissed
          }`}
        >
          {getCallOutcomeLabel(outcome)}
        </span>
      )
    },
  },
  { title: "Ring Time", name: "ring_time", type: "sort" },
  { title: "Talk Time", name: "talk_time", type: "sort" },
]

const contractorHeaders: UITypes.TableHeaderCell[] = [
  { title: "Contractor", name: "contractor", type: "default" },
  { title: "Total Calls", name: "total_calls", type: "default" },
  { title: "Answered Rate", name: "answered_rate", type: "default" },
  { title: "Missed Calls", name: "missed_calls", type: "default" },
  { title: "Duration", name: "duration", type: "default" },
  { title: "Ring Time", name: "ring_time", type: "default" },
]

const chartSeries = [
  { label: "Total Calls", color: "#1689ff", key: "total_calls" as const },
  { label: "Answered Calls", color: "#0bad63", key: "answered_calls" as const },
  { label: "Missed Calls", color: "#ff3131", key: "missed_calls" as const },
]

const formatMinutesSeconds = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

const formatTalkTotal = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}

const formatDate = (value: string) => {
  const date = dayjs(value)
  return date.isValid() ? date.format("LLL") : value
}

const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`
}

const convertCallLogToRows = (calls: ApiTypes.Res.VendorCallLogReport["call_log"] = []): UITypes.TableBodyRow[] =>
  calls.map((call, index) => ({
    occurred_at: { value: formatDate(call.occurred_at) },
    contractor: { value: call.contractor ?? "—" },
    customer: { value: call.customer_name ?? "—" },
    calling_number: { value: call.calling_number ?? "—" },
    outcome: { value: call.outcome },
    ring_time: { value: formatDuration(call.ring_time_seconds) },
    talk_time: { value: formatDuration(call.talk_time_seconds) },
    rowSettings: { id: index, item: call },
  }))

export const Calls = () => {
  const [getCallLog, { data: callLogData, isFetching: isCallLogFetching }] = useLazyGetVendorCallLogQuery()
  const [getCallStats, { currentData: callStats, isError: hasCallStatsError }] = useLazyGetVendorCallStatsQuery()
  const [
    getContractorPerformance,
    { currentData: performanceData, isFetching: isPerformanceFetching, isError: hasPerformanceError },
  ] = useLazyGetVendorCallContractorPerformanceQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const page = Number(urlQueryParams.page) || 1
  const perPage = Number(urlQueryParams.perPage ?? urlQueryParams.per_page) || 25
  const performancePage = Number(urlQueryParams.performance_page) || 1
  const performancePerPage = Number(urlQueryParams.performance_perPage) || 10
  const sortColumn = urlQueryParams.orderColumn
  const sortDirection = urlQueryParams.orderType === "ASC" ? "asc" : "desc"
  const callLogSortColumn = ["ring_time", "talk_time", "contractor", "customer"].includes(sortColumn || "")
    ? sortColumn
    : undefined
  const dateRange = useMemo(() => {
    if (!urlQueryParams.calls_period_start) return undefined
    return {
      date_from: urlQueryParams.calls_period_start,
      date_to: urlQueryParams.calls_period_end,
    }
  }, [urlQueryParams.calls_period_end, urlQueryParams.calls_period_start])
  const periodEndDate = dateRange?.date_to ?? new Date().toISOString().slice(0, 10)
  const selectedPeriod = getFilterValueFromParams(urlQueryParams, "calls_period")
  const periodLabel = dateRange
    ? `Showing Period: ${
        selectedPeriod === "custom"
          ? formatCustomDateRangeLabel(new Date(dateRange.date_from), new Date(periodEndDate))
          : ({ today: "Today", "last-7-days": "Last 7 days", "last-month": "Last Month" }[selectedPeriod] ??
            "Last 7 days")
      }`
    : ""

  useEffect(() => {
    if (sortColumn === "occurred_at") {
      setUrlQueryParams({ orderColumn: null, orderType: null })
    }
  }, [setUrlQueryParams, sortColumn])

  useEffect(() => {
    if (!urlQueryParams.calls_period_start && !urlQueryParams.calls_period_end) {
      setUrlQueryParams(getDateRangeParams("last-7-days", "calls_period"))
    }
  }, [setUrlQueryParams, urlQueryParams.calls_period_end, urlQueryParams.calls_period_start])

  useEffect(() => {
    if (!dateRange) return
    getCallStats(dateRange)
  }, [dateRange, getCallStats])

  useEffect(() => {
    if (!dateRange) return
    getContractorPerformance({
      ...dateRange,
      page: performancePage,
      per_page: performancePerPage,
    })
  }, [dateRange, getContractorPerformance, performancePage, performancePerPage])

  useEffect(() => {
    const sortBy =
      callLogSortColumn === "ring_time" ? "ring_time" : callLogSortColumn === "talk_time" ? "call_duration" : undefined

    getCallLog({
      page,
      per_page: perPage,
      contractor: urlQueryParams.contractor,
      customer: urlQueryParams.customer,
      date_from: urlQueryParams.calls_period_start,
      date_to: urlQueryParams.calls_period_end,
      sort_by: sortBy,
      sort_direction: sortBy ? sortDirection : undefined,
    })
  }, [
    getCallLog,
    page,
    perPage,
    callLogSortColumn,
    sortDirection,
    urlQueryParams.contractor,
    urlQueryParams.customer,
    urlQueryParams.calls_period_start,
    urlQueryParams.calls_period_end,
  ])

  const rows = useMemo(() => {
    const result = convertCallLogToRows(callLogData?.call_log)
    if (sortColumn !== "contractor" && sortColumn !== "customer") return result

    const direction = sortDirection === "asc" ? 1 : -1
    return result.sort(
      (left, right) =>
        String(left[sortColumn]?.value ?? "").localeCompare(String(right[sortColumn]?.value ?? "")) * direction,
    )
  }, [callLogData?.call_log, sortColumn, sortDirection])
  const paginationMeta = useMemo(() => {
    if (!callLogData?.pagination) return undefined
    const { current_page, per_page, has_next_page } = callLogData.pagination
    const total = has_next_page ? current_page * per_page + 1 : (current_page - 1) * per_page + rows.length
    return {
      current_page,
      last_page: Math.max(1, Math.ceil(total / per_page)),
      from: rows.length ? (current_page - 1) * per_page + 1 : 0,
      to: (current_page - 1) * per_page + rows.length,
      per_page,
      total,
    }
  }, [callLogData?.pagination, rows.length])

  const contractorRows: UITypes.TableBodyRow[] = (performanceData?.contractors ?? []).map((contractor, index) => ({
    contractor: { value: contractor.contractor },
    total_calls: { value: contractor.total_calls },
    answered_rate: { value: `${contractor.answered_rate}%` },
    missed_calls: { value: contractor.missed_calls },
    duration: { value: formatMinutesSeconds(contractor.average_talk_time_seconds) },
    ring_time: { value: `${contractor.average_ring_time_seconds}s` },
    rowSettings: { id: contractor.contractor_id ?? index, item: contractor },
  }))
  const contractorPaginationMeta = useMemo(() => {
    if (!performanceData?.pagination) return undefined
    const { current_page, per_page, has_next_page } = performanceData.pagination
    const total = has_next_page ? current_page * per_page + 1 : (current_page - 1) * per_page + contractorRows.length
    return {
      current_page,
      last_page: Math.max(1, Math.ceil(total / per_page)),
      from: contractorRows.length ? (current_page - 1) * per_page + 1 : 0,
      to: (current_page - 1) * per_page + contractorRows.length,
      per_page,
      total,
    }
  }, [contractorRows.length, performanceData?.pagination])

  const metrics = [
    { icon: "☎", title: "Total calls", value: callStats?.total_calls.toLocaleString() ?? "—", note: "", tone: "blue" },
    {
      icon: "↗",
      title: "Answered calls",
      value: callStats?.answered_calls.toLocaleString() ?? "—",
      note: `${callStats?.answered_calls.toLocaleString() ?? "—"} answered calls`,
      tone: "green",
    },
    {
      icon: "⌕",
      title: "Missed calls",
      value: callStats?.missed_calls.toLocaleString() ?? "—",
      note: callStats
        ? `${callStats.missed_breakdown.no_answer} No Answer | ${callStats.missed_breakdown.busy} Busy | ${callStats.missed_breakdown.failed} Failed`
        : "",
      tone: "red",
    },
    {
      icon: "◷",
      title: "Answer-time median",
      value: callStats ? `${callStats.median_answer_time_seconds}s` : "—",
      note: "",
      tone: "purple",
    },
    {
      icon: "♟",
      title: "Talk-time average",
      value: callStats ? formatMinutesSeconds(callStats.average_talk_time_seconds) : "—",
      note: callStats ? `Total talk time: ${formatTalkTotal(callStats.total_talk_time_seconds)}` : "",
      tone: "orange",
    },
    {
      icon: "▦",
      title: "Contractor adoption",
      value: callStats ? `${callStats.contractor_adoption_percent}%` : "—",
      note: callStats ? `${callStats.adopted_contractors} of ${callStats.active_contractors} active contractors` : "",
      tone: "violet",
    },
  ]
  const trendData = {
    labels: callStats?.daily_calls.map(item => dayjs(item.date).format("MMM D")) ?? [],
    datasets: chartSeries.map(series => ({
      label: series.label,
      data: callStats?.daily_calls.map(item => item[series.key]) ?? [],
      borderColor: series.color,
      backgroundColor: "transparent",
      pointBackgroundColor: series.color,
      tension: 0,
      pointRadius: 3,
    })),
  }
  const maxDailyCalls = Math.max(100, ...trendData.datasets.flatMap(dataset => dataset.data))
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      y: { min: 0, max: Math.ceil(maxDailyCalls / 25) * 25, ticks: { stepSize: 25 }, grid: { color: "#e7edf4" } },
      x: { grid: { color: "#e7edf4" } },
    },
  }

  return (
    <div className={styles.callsPage}>
      <ContentWrapper
        title="Calls"
        renderHeaderAccessoryView={() => (
          <div className={styles.summaryHeading}>
            <DateRangeTabsFilter
              paramName="calls_period"
              urlQueryParams={urlQueryParams}
              onParamsChange={params =>
                setUrlQueryParams({
                  ...params,
                  page: 1,
                  performance_page: 1,
                  date_start: null,
                  date_end: null,
                })
              }
              filterGroups={[["today"], ["last-7-days"], ["last-month"], ["custom"]]}
              maxRangeInYears={1}
            />
          </div>
        )}
      >
        <div className={styles.metricsGrid}>
          {metrics.map(metric => (
            <article className={styles.metricCard} key={metric.title}>
              <span className={`${styles.metricIcon} ${styles[metric.tone]}`} aria-hidden="true">
                {metric.icon}
              </span>
              <div className={styles.metricDetails}>
                <span className={styles.metricTitle}>{metric.title}</span>
                <strong>{metric.value}</strong>
                {metric.note && <small>{metric.note}</small>}
              </div>
            </article>
          ))}
        </div>
        <div className={styles.chart}>
          <Line data={trendData} options={chartOptions} />
        </div>
        <div className={styles.legend}>
          {chartSeries.map(dataset => (
            <span key={dataset.label}>
              <i style={{ backgroundColor: dataset.color }} />
              {dataset.label}
            </span>
          ))}
        </div>
        {hasCallStatsError && <p role="alert">Call statistics could not be loaded.</p>}
      </ContentWrapper>
      <ContentWrapper
        title="Contractor Performance"
        renderHeaderAccessoryView={() => <span className={styles.periodLabel}>{periodLabel}</span>}
      >
        <TableWrapper headerCells={contractorHeaders} data={contractorRows} isLoading={isPerformanceFetching} />
        <Pagination
          meta={contractorPaginationMeta}
          queryParamPrefix="performance"
          defaultPerPage={10}
          numbersOfPage={[10, 20, 50, 100]}
          singleLine
        />
        {hasPerformanceError && <p role="alert">Contractor performance could not be loaded.</p>}
      </ContentWrapper>
      <ContentWrapper
        title="Call Log"
        renderHeaderAccessoryView={() => <span className={styles.periodLabel}>{periodLabel}</span>}
      >
        <TableWrapper headerCells={headerCells} data={rows} isLoading={isCallLogFetching} />
        <Pagination meta={paginationMeta} perPage={perPage} />
      </ContentWrapper>
    </div>
  )
}
